// ReviewsCarousel.jsx - Main component that imports all the other components
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";

// Import the separated components
import ReviewCard from "./ReviewCard";
import RatingsOverview from "./RatingsOverview";
import ProductImagesGallery from "./ProductImagesGallery";
import ProductImagesModal from "./ProductImagesModal";

const ReviewsCarousel = () => {
  // State for reviews and UI
  const [reviews, setReviews] = useState([]);
  const [ratings, setRatings] = useState({
    averageRating: 0,
    totalRatings: 0,
    ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    reviewsCount: 0,
  });
  const [allImages, setAllImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterRating] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Modal state - FIXED: Added selectedModalImages state to store images when modal is opened
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedModalImages, setSelectedModalImages] = useState([]);

  // Cards per page (2 columns × 4 rows)
  const cardsPerPage = 8;

  // Mock data for fallback - moved into the useEffect to avoid the warning
  const mockReviews = useMemo(
    () => [
      {
        id: "mock-1",
        name: "Angelica Urdaneta",
        country: "United States",
        rating: 5,
        review:
          "Excellent testosterone booster supplement ideal for our muscle provides incredible energy and you get incredible results",
      },
      {
        id: "mock-2",
        name: "Elena W",
        country: "United States",
        rating: 5,
        review:
          "Got this for my husband, and he's really happy with the results! He has more energy, better focus, and feels overall stronger. I love that it's made with natural ingredients. Definitely a great addition to his routine!",
      },
      {
        id: "mock-3",
        name: "Gianna Jones",
        country: "United States",
        rating: 5,
        review: "bought these for my boyfriend and he loves them!",
      },
      {
        id: "mock-4",
        name: "Shalom Damabel",
        country: "United States",
        rating: 5,
        review:
          "Already seen a big boost in energy since taking these! Absolutely must have!",
      },
      {
        id: "mock-5",
        name: "Daria",
        country: "United States",
        rating: 5,
        review:
          "My brother tried this testosterone booster and was absolutely thrilled! He gained more energy, strength, and vitality. His mood improved, and he felt a renewed joy for life and motivation for workouts. He definitely recommends it to anyone looking for a real boost of energy!",
      },
      {
        id: "mock-6",
        name: "candelapurastorecandelapurastore",
        country: "United States",
        rating: 5,
        review: "dynamic formula good all time",
      },
    ],
    []
  );

  const mockRatings = useMemo(
    () => ({
      averageRating: 5,
      totalRatings: 9,
      ratingCounts: {
        5: 9,
        4: 0,
        3: 0,
        2: 0,
        1: 0,
      },
      reviewsCount: 6,
    }),
    []
  );

  const mockImages = useMemo(
    () => ({
      images: [
        {
          url: "https://m.media-amazon.com/images/I/61be-dFiuCL.jpg",
          reviewId: "R2XEBC8TT2XGHC",
          imageId: "61be-dFiuCL",
        },
        {
          url: "https://m.media-amazon.com/images/I/61kPqIipGrL.jpg",
          reviewId: "R2XEBC8TT2XGHC",
          imageId: "61kPqIipGrL",
        },
      ],
      lastUpdated: new Date().toISOString(),
    }),
    []
  );

  // Function to randomly select 4 images from the image pool
  const selectRandomImages = useCallback(() => {
    if (allImages.length === 0) return [];

    // Create a copy of the images array to shuffle
    const shuffled = [...allImages];

    // Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Take the first 4 images, or all if less than 4
    return shuffled.slice(0, Math.min(4, shuffled.length));
  }, [allImages]);

  // Initialize displayed images and set up the image rotation interval
  useEffect(() => {
    if (allImages.length > 0) {
      setDisplayedImages(selectRandomImages());

      // Set up interval to rotate images only when modal is closed
      const intervalId = setInterval(() => {
        if (!isImageModalOpen) {
          setDisplayedImages(selectRandomImages());
        }
      }, 10000); // 10 seconds in milliseconds

      // Clean up the interval when component unmounts
      return () => clearInterval(intervalId);
    }
  }, [allImages, selectRandomImages, isImageModalOpen]);

  // Fetch reviews, ratings, and images from API with fallback to mock data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch ratings
        const ratingsResponse = await fetch(
          "https://api.zivbiolabs.com/api/ratings"
        );

        if (!ratingsResponse.ok) {
          throw new Error("Failed to fetch ratings");
        }

        const ratingsData = await ratingsResponse.json();

        // Convert percentage strings to numeric values if needed
        const processedRatingCounts = {};
        Object.keys(ratingsData.ratingCounts || {}).forEach((key) => {
          const value = ratingsData.ratingCounts[key];
          // If value is a percentage string like "100%", convert to number
          if (typeof value === "string" && value.endsWith("%")) {
            const numericValue = parseInt(value, 10);
            // Calculate actual count based on percentage and totalRatings
            processedRatingCounts[key] = Math.round(
              (numericValue / 100) * ratingsData.totalRatings
            );
          } else {
            processedRatingCounts[key] = value;
          }
        });

        setRatings({
          ...ratingsData,
          ratingCounts: processedRatingCounts,
        });

        // Fetch reviews
        const reviewsResponse = await fetch(
          "https://api.zivbiolabs.com/api/reviews"
        );

        if (!reviewsResponse.ok) {
          throw new Error("Failed to fetch reviews");
        }

        const reviewsData = await reviewsResponse.json();

        // Add unique IDs to existing reviews if they don't have them
        const reviewsWithIds = reviewsData.map((review, index) => ({
          ...review,
          id: review.id || `api-${Date.now()}-${index}`,
        }));

        setReviews(reviewsWithIds);
        setFilteredReviews(reviewsWithIds);

        // Fetch images
        const imagesResponse = await fetch(
          "https://api.zivbiolabs.com/api/images"
        );

        if (!imagesResponse.ok) {
          throw new Error("Failed to fetch images");
        }

        const imagesData = await imagesResponse.json();
        setAllImages(imagesData.images || []);
      } catch (error) {
        console.error("Error fetching data:", error);

        // Use mock data instead
        setReviews(mockReviews);
        setFilteredReviews(mockReviews);
        setRatings(mockRatings);
        setAllImages(mockImages.images || []);

        // Don't show error UI when using fallback data
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [mockReviews, mockRatings, mockImages]);

  // Apply filter when filterRating changes
  useEffect(() => {
    if (filterRating === 0) {
      // Show all reviews
      setFilteredReviews(reviews);
    } else {
      // Filter by rating
      setFilteredReviews(
        reviews.filter((review) => Math.round(review.rating) === filterRating)
      );
    }
    // Reset current index when filter changes
    setCurrentIndex(0);
  }, [filterRating, reviews]);

  // Navigation functions
  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) =>
      Math.min(prev + 2, Math.max(0, filteredReviews.length - cardsPerPage))
    );
  }, [filteredReviews.length, cardsPerPage]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 2, 0));
  }, []);

  const firstSlide = useCallback(() => {
    setCurrentIndex(0);
  }, []);

  const lastSlide = useCallback(() => {
    setCurrentIndex(Math.max(0, filteredReviews.length - cardsPerPage));
  }, [filteredReviews.length, cardsPerPage]);

  // Modal functions - FIXED: Update modal functions to use the current set of displayed images
  const openImageModal = useCallback((image, index) => {
    // When opening modal, store the current set of displayed images
    setSelectedModalImages([...displayedImages]);
    setCurrentImageIndex(index);
    setIsImageModalOpen(true);
  }, [displayedImages]);

  const closeImageModal = useCallback(() => {
    setIsImageModalOpen(false);
  }, []);

  const nextImage = useCallback(() => {
    if (selectedModalImages.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedModalImages.length);
    }
  }, [selectedModalImages]);

  const prevImage = useCallback(() => {
    if (selectedModalImages.length > 0) {
      setCurrentImageIndex(
        (prev) => (prev - 1 + selectedModalImages.length) % selectedModalImages.length
      );
    }
  }, [selectedModalImages]);

  return (
    <section className="py-8 md:py-12 bg-gradient-to-b from-[#e9b320] to-yellow-50">
      <div className="max-w-[95%] lg:max-w-6xl mx-auto px-4 relative">
        <h2
          className="[text-shadow:1px_0_0_white,2px_0_0_white,1px_1px_0_white,2px_2px_0_white] text-6xl md:text-8xl text-center mb-8 pt-8 text-[#a41e24]"
          style={{
            fontFamily: "Headlined, sans-serif",
          }}
        >
          REAL RESULTS
        </h2>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8 bg-white rounded-lg shadow-md">
            <p className="text-red-600">{error}</p>
          </div>
        ) : (
          <>
            {/* Top section with ratings and product images side by side */}
            <div className="bg-white rounded-lg p-6 mb-8 shadow-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left side - Ratings */}
                <div>
                  <RatingsOverview ratings={ratings} />
                </div>

                {/* Right side - Product Images */}
                <div>
                  <ProductImagesGallery 
                    images={displayedImages} 
                    onImageClick={openImageModal} 
                  />
                </div>
              </div>
            </div>

            {/* Customer Reviews Section */}
            <div className="mb-6 flex justify-center items-center">
              <h2
                className="[text-shadow:1px_0_0_white,2px_0_0_white,1px_1px_0_white,2px_2px_0_white] text-[2.5rem] md:text-6xl text-center mb-8 pt-8 text-[#a41e24]"
                style={{
                  fontFamily: "Headlined, sans-serif",
                }}
              >
                WHAT OUR CUSTOMERS SAY
              </h2>
            </div>

            {filteredReviews.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredReviews
                    .slice(currentIndex, currentIndex + cardsPerPage)
                    .map((review, index) => (
                      <div key={review.id || index} className="mb-4">
                        <ReviewCard review={review} />
                      </div>
                    ))}
                </div>

                {/* Navigation buttons */}
                <div className="flex justify-center gap-4 mt-6">
                  <button
                    type="button"
                    onClick={firstSlide}
                    className="bg-white p-2 rounded-full shadow-lg hover:shadow-xl transition-shadow"
                    aria-label="First reviews"
                  >
                    <ChevronsLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                  </button>
                  <button
                    type="button"
                    onClick={prevSlide}
                    className="bg-white p-2 rounded-full shadow-lg hover:shadow-xl transition-shadow"
                    aria-label="Previous reviews"
                  >
                    <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                  </button>
                  <button
                    type="button"
                    onClick={nextSlide}
                    className="bg-white p-2 rounded-full shadow-lg hover:shadow-xl transition-shadow"
                    aria-label="Next reviews"
                  >
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                  </button>
                  <button
                    type="button"
                    onClick={lastSlide}
                    className="bg-white p-2 rounded-full shadow-lg hover:shadow-xl transition-shadow"
                    aria-label="Last reviews"
                  >
                    <ChevronsRight className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-8 bg-white rounded-lg shadow-md">
                <p className="text-gray-600">
                  No reviews match your filter criteria.
                </p>
              </div>
            )}

            {/* Image Modal - FIXED: Now using selectedModalImages */}
            <ProductImagesModal 
              isOpen={isImageModalOpen}
              onClose={closeImageModal}
              images={selectedModalImages}
              currentIndex={currentImageIndex}
              onPrevImage={prevImage}
              onNextImage={nextImage}
            />
          </>
        )}
      </div>
    </section>
  );
};

export default ReviewsCarousel;